import React, { FC, ChangeEventHandler, useCallback, useMemo } from 'react';

import {
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowMe,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
} from 'src/interfaces/reservation_flow';
import { RSBorderedListItemCheckRadio } from 'src/reservation-page/bordered-list/RSBorderedListItemCheckRadio';

import Visa from 'src/templates/merchants/resources/book/components/card-brands/Visa.svg';
import MasterCard from 'src/templates/merchants/resources/book/components/card-brands/MasterCard.svg';
import JCB from 'src/templates/merchants/resources/book/components/card-brands/JCB.svg';
import Amex from 'src/templates/merchants/resources/book/components/card-brands/Amex.svg';
import Diners from 'src/templates/merchants/resources/book/components/card-brands/Diners.svg';
import Discover from 'src/templates/merchants/resources/book/components/card-brands/Discover.svg';

export interface RegisteredCreditCardItemProps {
  value?:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  onChange: (
    paymentMethod: ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ) => void;
  onBlur: () => void;
  userCreditCard: ReservationFlowMe['credit_card'];
  hundleSelected?: () => void;
}

export const RegisteredCreditCardItem: FC<RegisteredCreditCardItemProps> = ({
  value,
  onChange,
  onBlur,
  userCreditCard,
  hundleSelected,
}) => {
  const isSelected = useMemo(() => {
    if (typeof value === 'undefined') return false;

    return value.type === 'credit_card';
  }, [value]);

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (!event.target.checked) return;

      onChange({
        type: 'credit_card',
        stripe_credit_card_token: null,
        is_restore_credit_card: true,
        is_store_credit_card: false,
        creditCardInfo: value?.creditCardInfo ?? {},
      });
      onBlur();
      if (hundleSelected) {
        hundleSelected();
      }
    },
    [onChange, value?.creditCardInfo, onBlur, hundleSelected],
  );

  return (
    <RSBorderedListItemCheckRadio
      name="subscriptionPaymentMethod"
      checked={isSelected}
      onChange={onInputChange}
      leftContent={
        <div className="flex items-center space-x-3">
          {(() => {
            switch (userCreditCard?.brand_name) {
              case 'Visa':
                return (
                  <Visa
                    width={48}
                    height={15}
                    viewBox="0 0 160 52"
                    className="block"
                  />
                );
              case 'MasterCard':
                return (
                  <MasterCard
                    width={48}
                    height={30}
                    viewBox="0 0 162 100"
                    className="block"
                  />
                );
              case 'JCB':
                return (
                  <JCB
                    width={48}
                    height={35}
                    viewBox="0 0 135 100"
                    className="block"
                  />
                );
              case 'American Express':
                return (
                  <Amex
                    width={48}
                    height={48}
                    viewBox="0 0 101 100"
                    className="block"
                  />
                );
              case 'Diners Club':
                return (
                  <Diners
                    width={48}
                    height={35}
                    viewBox="0 0 136 100"
                    className="block"
                  />
                );
              case 'Discover':
                return (
                  <Discover
                    width={48}
                    height={30}
                    viewBox="0 0 157 100"
                    className="block"
                  />
                );
              default:
                return null;
            }
          })()}
          <div className="rs-font text-16 text-gray-1000 pc:text-16 font-normal">{`**********${userCreditCard?.tail_digits}`}</div>
        </div>
      }
    />
  );
};
